import React, { useState, useCallback, useMemo } from "react";
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Chip,
  Stack,
  Divider,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
} from "@mui/icons-material";
import {
  format,
  addMonths,
  subMonths,
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  isSameDay,
  isWithinInterval,
  isSameMonth,
  addDays,
  subDays,
  startOfWeek,
  endOfWeek,
  isBefore,
  isAfter,
  isToday,
} from "date-fns";

// Custom DateRangePickerDay component for enhanced day rendering
const DateRangePickerDay = ({
  day,
  isSelected,
  isInRange,
  isStartDate,
  isEndDate,
  isOutsideCurrentMonth,
  onClick,
  disabled,
  ...props
}) => {
  const theme = useTheme();

  const getDayStyles = () => {
    const baseStyles = {
      width: 40,
      height: 40,
      borderRadius: "50%",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      cursor: disabled ? "not-allowed" : "pointer",
      border: "none",
      backgroundColor: "transparent",
      fontSize: "0.875rem",
      fontWeight: 400,
      transition: "all 0.2s ease",
      position: "relative",
      "&:hover": {
        backgroundColor: disabled ? "transparent" : theme.palette.action.hover,
      },
    };

    if (isOutsideCurrentMonth) {
      return {
        ...baseStyles,
        color: theme.palette.text.disabled,
        opacity: 0.5,
      };
    }

    if (isSelected) {
      if (isStartDate || isEndDate) {
        return {
          ...baseStyles,
          backgroundColor: theme.palette.primary.main,
          color: theme.palette.primary.contrastText,
          fontWeight: 600,
          "&:hover": {
            backgroundColor: theme.palette.primary.dark,
          },
        };
      }
    }

    if (isInRange) {
      return {
        ...baseStyles,
        backgroundColor: theme.palette.primary.light,
        color: theme.palette.primary.contrastText,
        borderRadius: 0,
        "&:hover": {
          backgroundColor: theme.palette.primary.main,
        },
      };
    }

    if (isToday(day)) {
      return {
        ...baseStyles,
        border: `2px solid ${theme.palette.primary.main}`,
        fontWeight: 600,
      };
    }

    return baseStyles;
  };

  return (
    <button
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        if (!disabled && onClick) {
          onClick(day);
        }
      }}
      disabled={disabled}
      style={getDayStyles()}
      {...props}
    >
      {format(day, "d")}
    </button>
  );
};

const CalendarDateRangePicker = ({
  value = null,
  defaultValue = null,
  onChange,
  calendars = 2,
  currentMonthCalendarPosition = 1,
  disabled = false,
  readOnly = false,
  disableFuture = false,
  disablePast = false,
  minDate,
  maxDate,
  slots = {},
  slotProps = {},
  sx = {},
  ...props
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  // Use controlled or uncontrolled value
  const [internalValue, setInternalValue] = useState(
    defaultValue || [null, null]
  );
  const isControlled = value !== null;
  const currentValue = isControlled ? value : internalValue;

  // Current month state
  const [currentMonth, setCurrentMonth] = useState(() => {
    const now = new Date();
    if (currentMonthCalendarPosition === 1) {
      return now;
    } else if (currentMonthCalendarPosition === 2) {
      return subMonths(now, 1);
    } else {
      return subMonths(now, 2);
    }
  });

  // Custom day component
  const DayComponent = slots.day || DateRangePickerDay;

  // Generate calendar months
  const calendarMonths = useMemo(() => {
    const months = [];
    for (let i = 0; i < calendars; i++) {
      const month = addMonths(
        currentMonth,
        i - (currentMonthCalendarPosition - 1)
      );
      months.push(month);
    }
    return months;
  }, [currentMonth, calendars, currentMonthCalendarPosition]);

  // Generate days for a month
  const getDaysForMonth = useCallback((month) => {
    const start = startOfWeek(startOfMonth(month));
    const end = endOfWeek(endOfMonth(month));
    return eachDayOfInterval({ start, end });
  }, []);

  // Check if date is disabled
  const isDateDisabled = useCallback(
    (date) => {
      if (disabled || readOnly) return true;
      if (disableFuture && isAfter(date, new Date())) return true;
      if (disablePast && isBefore(date, new Date())) return true;
      if (minDate && isBefore(date, minDate)) return true;
      if (maxDate && isAfter(date, maxDate)) return true;
      return false;
    },
    [disabled, readOnly, disableFuture, disablePast, minDate, maxDate]
  );

  // Check if date is in range
  const isDateInRange = useCallback(
    (date) => {
      if (!currentValue || !currentValue[0] || !currentValue[1]) return false;
      return isWithinInterval(date, {
        start: currentValue[0],
        end: currentValue[1],
      });
    },
    [currentValue]
  );

  // Check if date is selected (start or end)
  const isDateSelected = useCallback(
    (date) => {
      if (!currentValue) return false;
      return currentValue.some(
        (selectedDate) => selectedDate && isSameDay(date, selectedDate)
      );
    },
    [currentValue]
  );

  // Check if date is start date
  const isStartDate = useCallback(
    (date) => {
      return (
        currentValue && currentValue[0] && isSameDay(date, currentValue[0])
      );
    },
    [currentValue]
  );

  // Check if date is end date
  const isEndDate = useCallback(
    (date) => {
      return (
        currentValue && currentValue[1] && isSameDay(date, currentValue[1])
      );
    },
    [currentValue]
  );

  // Handle date click
  const handleDateClick = useCallback(
    (date) => {
      console.log("Date clicked:", date);
      if (isDateDisabled(date)) return;

      let newValue;
      if (
        !currentValue ||
        !currentValue[0] ||
        (currentValue[0] && currentValue[1])
      ) {
        // Start new selection
        newValue = [date, null];
      } else {
        // Complete selection
        if (isBefore(date, currentValue[0])) {
          newValue = [date, currentValue[0]];
        } else {
          newValue = [currentValue[0], date];
        }
      }

      console.log("New value:", newValue);

      if (!isControlled) {
        setInternalValue(newValue);
      }

      if (onChange) {
        console.log("Calling onChange with:", newValue);
        onChange(newValue);
      }
    },
    [currentValue, isControlled, onChange, isDateDisabled]
  );

  // Handle month navigation
  const handlePreviousMonth = useCallback(() => {
    setCurrentMonth((prev) => subMonths(prev, 1));
  }, []);

  const handleNextMonth = useCallback(() => {
    setCurrentMonth((prev) => addMonths(prev, 1));
  }, []);

  // Week day headers
  const weekDays = ["S", "M", "T", "W", "T", "F", "S"];

  return (
    <Box
      elevation={1}
      sx={{
        py: 2,
        width: "100%",
        borderRadius: "10px",
        border: "1px solid #e0e0e0",

        ...sx,
      }}
      {...props}
    >
      {/* Header with navigation */}
      {/* Selected range display */}
      {currentValue && (currentValue[0] || currentValue[1]) && (
        <Box sx={{ mb: 2 }}>
          <Stack
            direction="row"
            justifyContent="space-between"
            spacing={1}
            flexWrap="wrap"
          >
            {currentValue[0] && (
              <Chip
                label={`Start: ${format(currentValue[0], "MMM dd, yyyy")}`}
                color="primary"
                variant="outlined"
                size="small"
              />
            )}
            {currentValue[1] && (
              <Chip
                label={`End: ${format(currentValue[1], "MMM dd, yyyy")}`}
                color="primary"
                variant="outlined"
                size="small"
              />
            )}
          </Stack>
        </Box>
      )}
      {/* Calendars */}
      <Stack
        direction="column"
        justifyContent="center"
        alignItems="center"
        sx={{ maxWidth: "310px", mx: "auto" }}
      >
        {calendarMonths.map((month, index) => (
          <Box key={index} sx={{ mx: "auto" }}>
            {/* Month header */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 1,
              }}
            >
              <IconButton
                size="small"
                onClick={handlePreviousMonth}
                disabled={disabled || readOnly}
                sx={{ visibility: index === 0 ? "visible" : "hidden" }}
              >
                <ChevronLeftIcon />
              </IconButton>

              <Typography variant="h6" component="h3">
                {format(month, "MMMM yyyy")}
              </Typography>

              <IconButton
                size="small"
                onClick={handleNextMonth}
                disabled={disabled || readOnly}
                sx={{
                  visibility: index === calendars - 1 ? "visible" : "hidden",
                }}
              >
                <ChevronRightIcon />
              </IconButton>
            </Box>

            {/* Week day headers */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(7, 1fr)",
                mb: 1,
              }}
            >
              {weekDays.map((day, dayIndex) => (
                <Box
                  key={dayIndex}
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: 36,
                    fontSize: "0.75rem",
                    fontWeight: 600,
                    color: theme.palette.text.secondary,
                  }}
                >
                  {day}
                </Box>
              ))}
            </Box>

            {/* Calendar days */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(7, 1fr)",
                rowGap: 1,
              }}
            >
              {getDaysForMonth(month).map((day, dayIndex) => {
                const isOutsideCurrentMonth = !isSameMonth(day, month);
                const isSelected = isDateSelected(day);
                const isInRange = isDateInRange(day);
                const isStart = isStartDate(day);
                const isEnd = isEndDate(day);
                const isDisabled = isDateDisabled(day);

                return (
                  <Box
                    key={dayIndex}
                    sx={{ display: "flex", justifyContent: "center" }}
                  >
                    <DayComponent
                      day={day}
                      isSelected={isSelected}
                      isInRange={isInRange}
                      isStartDate={isStart}
                      isEndDate={isEnd}
                      isOutsideCurrentMonth={isOutsideCurrentMonth}
                      disabled={isDisabled}
                      onClick={handleDateClick}
                      {...slotProps.day}
                    />
                  </Box>
                );
              })}
            </Box>
          </Box>
        ))}
      </Stack>
    </Box>
  );
};

export default CalendarDateRangePicker;
